import React, { useMemo, useState } from 'react'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Grid,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import {
    ArrowForward as ArrowForwardIcon,
    ExpandMore as ExpandMoreIcon,
    HelpOutline as HelpIcon,
    Search as SearchIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'

const ORANGE = '#FF8A00'
const cardSx = {
    borderRadius: 6,
    background: 'rgba(16, 11, 38, 0.92)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
}

const FAQ = () => {
    const navigate = useNavigate()
    const { lang } = useLanguage()
    const isFr = lang === 'fr'
    const [query, setQuery] = useState('')

    const items = useMemo(
        () => [
            {
                q: isFr
                    ? 'Ou puis-je voir mes recus et mes preuves ?'
                    : 'Where can I see my receipts and proof?',
                a: isFr
                    ? 'Ouvrez Transactions. Cette page est votre piste de preuve pour envois, factures, dons, taxes, et autres activites finalisees.'
                    : 'Open Transactions. That page is your proof trail for send money, bills, donations, tax, and other completed activity.',
            },
            {
                q: isFr
                    ? 'Comment fonctionne la tarification diaspora ?'
                    : 'How does diaspora pricing work?',
                a: isFr
                    ? 'Les utilisateurs diaspora entrent generalement d abord le montant local pour les services au pays, puis MojoPayr affiche clairement le debit dans la devise de residence, le change, et les frais avant le paiement.'
                    : 'Diaspora users usually enter the local amount first for services back home, then MojoPayr shows the home-currency debit, FX, and fee clearly before payment.',
            },
            {
                q: isFr
                    ? 'Comment fonctionnent les paiements locaux ?'
                    : 'How do local payments work?',
                a: isFr
                    ? 'Les utilisateurs locaux restent dans leur marche de residence et utilisent un parcours tarifaire local plus leger, concu pour etre plus simple et plus abordable au quotidien.'
                    : 'Local users stay in their resident market and use the lighter local pricing path, designed to be simpler and more affordable for everyday use.',
            },
            {
                q: isFr
                    ? 'Puis-je faire un don ou contribuer et recevoir quand meme un recu ?'
                    : 'Can I donate or contribute and still get a receipt?',
                a: isFr
                    ? 'Oui. Les parcours de dons et contributions creent des recus qui peuvent etre rouverts plus tard dans Transactions.'
                    : 'Yes. Donations and contribution flows create receipt records that can be reopened later in Transactions.',
            },
            {
                q: isFr
                    ? 'Que faire si un recu partage ne s ouvre pas ?'
                    : 'What if a shared receipt does not open?',
                a: isFr
                    ? 'Une partie du partage de recu depend encore de l etat local du navigateur en mode limite. La piste de preuve dans Transactions reste la reference la plus fiable.'
                    : 'Some receipt-sharing behavior still depends on local browser state in limited fallback mode. The proof trail inside Transactions remains the most reliable reference.',
            },
            {
                q: isFr
                    ? 'Ou changer les parametres ou les details du profil ?'
                    : 'Where do I change settings or profile details?',
                a: isFr
                    ? 'Utilisez Profil pour les details d identite du compte, et Parametres pour les preferences comme notifications, langue, et comportement de l application.'
                    : 'Use Profile for account identity details, and Settings for preferences like notifications, language, and app behavior.',
            },
        ],
        [isFr]
    )

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase()
        if (!needle) return items
        return items.filter((item) => `${item.q} ${item.a}`.toLowerCase().includes(needle))
    }, [query, items])

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#09061B', py: { xs: 4, md: 6 } }}>
            <Container maxWidth="lg">
                <Card
                    sx={{
                        ...cardSx,
                        mb: 3,
                        overflow: 'hidden',
                        background:
                            'linear-gradient(135deg, rgba(17,10,40,0.98) 0%, rgba(70,36,188,0.96) 58%, rgba(255,138,0,0.16) 100%)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack spacing={1.4}>
                            <Chip label={isFr ? 'Support MojoPayr' : 'MojoPayr Support'} sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 900 }} />
                            <Typography component="h1" sx={{ color: '#fff', fontWeight: 950, fontSize: { xs: 30, md: 42 } }}>
                                FAQ
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.80)', maxWidth: 820 }}>
                                {isFr
                                    ? 'Reponses rapides aux questions les plus frequentes sur les paiements, recus, tarification diaspora, et usage quotidien de MojoPayr.'
                                    : 'Quick answers for the questions people ask most about payments, receipts, diaspora pricing, and everyday MojoPayr usage.'}
                            </Typography>
                        </Stack>
                    </CardContent>
                </Card>

                <Grid container spacing={3}>
                    <Grid item xs={12} lg={8}>
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2.6, md: 3 } }}>
                                <TextField
                                    fullWidth
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder={isFr ? 'Rechercher recus, paiements diaspora, factures, profil, parametres...' : 'Search receipts, diaspora payments, bills, profile, settings...'}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: 'rgba(255,255,255,0.55)' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        mb: 2.2,
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            borderRadius: 999,
                                            background: 'rgba(255,255,255,0.05)',
                                            '& fieldset': { borderColor: 'rgba(255,255,255,0.10)' },
                                        },
                                    }}
                                />

                                <Stack spacing={1.2}>
                                    {filtered.map((item) => (
                                        <Accordion
                                            key={item.q}
                                            disableGutters
                                            sx={{
                                                borderRadius: 4,
                                                overflow: 'hidden',
                                                bgcolor: 'rgba(255,255,255,0.03)',
                                                border: '1px solid rgba(255,255,255,0.06)',
                                                '&:before': { display: 'none' },
                                            }}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}>
                                                <Typography sx={{ color: '#fff', fontWeight: 900 }}>{item.q}</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Typography sx={{ color: 'rgba(255,255,255,0.74)', lineHeight: 1.8 }}>
                                                    {item.a}
                                                </Typography>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} lg={4}>
                        <Stack spacing={3}>
                            <Card sx={cardSx}>
                                <CardContent sx={{ p: 3 }}>
                                    <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mb: 1.3 }}>
                                        <HelpIcon sx={{ color: ORANGE }} />
                                        <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: 22 }}>
                                            {isFr ? 'Besoin de plus d aide ?' : 'Need more help?'}
                                        </Typography>
                                    </Stack>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.70)', mb: 2 }}>
                                        {isFr
                                            ? 'Si une reponse ici ne suffit pas, allez directement vers la bonne page de support au lieu de chercher partout sur le site.'
                                            : 'If an answer here is not enough, go directly to the right support page instead of hunting around the site.'}
                                    </Typography>
                                    <Stack spacing={1.2}>
                                        <Button variant="contained" onClick={() => navigate('/help-center')} sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 900, bgcolor: ORANGE, '&:hover': { bgcolor: '#ff9c2a' } }}>
                                            {isFr ? 'Ouvrir le centre d aide' : 'Open Help Center'}
                                        </Button>
                                        <Button variant="outlined" onClick={() => navigate('/contact')} endIcon={<ArrowForwardIcon />} sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 900, color: '#fff', borderColor: 'rgba(255,255,255,0.18)' }}>
                                            {isFr ? 'Contacter le support' : 'Contact support'}
                                        </Button>
                                        <Button variant="text" onClick={() => navigate('/transactions')} sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800, color: 'rgba(255,255,255,0.82)' }}>
                                            {isFr ? 'Ouvrir Transactions' : 'Open Transactions'}
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    )
}

export default FAQ
